import React, { useMemo, useState } from "react";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import API from "../api";

// NOA Value Pod Platform
// Multi-Step Lead Creation Wizard
// Save as: src/pages/AddLeadWizard.jsx

const SECTORS = [
  "Green Hydrogen",
  "Green Buildings",
  "Sustainable Agriculture",
  "Offshore Wind",
  "Green Mobility",
  "Battery Energy Storage Systems (BESS)",
  "Critical Minerals",
];

const VALUE_CHAIN_POSITIONS = [
  "Upstream",
  "Midstream",
  "Downstream",
  "Enabler",
];

const STAGES = [
  "Concept",
  "Prototype",
  "Pilot",
  "Demonstration",
  "Commercial Deployment",
  "Scale-Up",
];

const USER_TYPES = [
  "Startup",
  "Corporate",
  "Investor",
  "Advisor",
  "Government",
  "NGO",
  "Research Institution",
];

const FUNDING_SOURCES = ["Equity", "Bank Loan", "Grant", "Mixed", "Other"];

const STEPS = [
  "Basic",
  "Organization",
  "Financial & Impact",
  "Documents",
  "Review",
];

const initialForm = {
  // Basic
  title: "",
  sector: "",
  valueChainPosition: "",
  stage: "",
  country: "India",
  summary: "",

  // Organization
  organizationName: "",
  website: "",
  userType: "",
  contactInfo: {
    name: "",
    email: "",
    phone: "",
  },

  // Strategic
  offers: [],
  needs: [],
  capabilities: [],

  // Financial & Impact
  projectValue: "",
  fundingRequired: "",
  fundingSource: "",
  co2Reduction: "",
  jobsCreated: "",
  targetStartDate: "",
  commercializationDate: "",

  comments: "",

  // Documents
  documents: [],
};

export default function AddLeadWizard() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(initialForm);

  const progress = ((step + 1) / STEPS.length) * 100;

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateNested = (section, key, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const validateStep = () => {
    const e = {};

    if (step === 0) {
      if (!form.title) e.title = "Project title is required";
      if (!form.sector) e.sector = "Sector is required";
      if (!form.valueChainPosition)
        e.valueChainPosition = "Value chain position is required";
      if (!form.stage) e.stage = "Project stage is required";
    }

    if (step === 1) {
      if (!form.organizationName)
        e.organizationName = "Organization name is required";
      if (!form.contactInfo.name)
        e.contactName = "Contact name is required";
      if (!form.contactInfo.email)
        e.contactEmail = "Contact email is required";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const leadScore = useMemo(() => {
    let score = 0;
    if (form.title) score += 10;
    if (form.organizationName) score += 10;
    if (form.sector) score += 10;
    if (form.valueChainPosition) score += 10;
    if (form.offers.length) score += 15;
    if (form.needs.length) score += 15;
    if (form.capabilities.length) score += 10;
    if (form.co2Reduction) score += 10;
    return Math.min(Math.round(score), 100);
  }, [form]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await API.post("/leads", {
        ...form,
        leadScore,
      });
      alert("Lead created successfully!");
      setForm(initialForm);
      setStep(0);
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.msg || "Failed to create lead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex">
      <Sidebar />

      <div className="flex-1 bg-gray-50 min-h-screen">
        <Navbar />

        <div className="max-w-6xl mx-auto p-6">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="p-8 border-b">
              <h1 className="text-3xl font-bold">Create New Lead</h1>
              <p className="text-gray-600 mt-1">
                Build a high-quality opportunity for AI matching and Value Pod creation.
              </p>

              <div className="mt-6">
                <div className="flex justify-between text-sm mb-2">
                  <span>
                    Step {step + 1} of {STEPS.length}: {STEPS[step]}
                  </span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-6 gap-2 mt-6">
                {STEPS.map((label, index) => (
                  <div
                    key={label}
                    className={`text-xs text-center p-2 rounded-xl ${
                      index === step
                        ? "bg-green-100 text-green-700 font-semibold"
                        : index < step
                        ? "bg-gray-100"
                        : "text-gray-400"
                    }`}
                  >
                    {index < step ? <Check className="w-4 h-4 mx-auto" /> : label}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-8">{renderStep(step, form, update, updateNested, errors)}</div>

            <div className="px-8 pb-8 flex justify-between">
              <button
                type="button"
                onClick={back}
                disabled={step === 0}
                className="px-5 py-2.5 rounded-xl border disabled:opacity-50 flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>

              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={next}
                  className="px-5 py-2.5 rounded-xl bg-green-600 text-white flex items-center gap-2"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2.5 rounded-xl bg-green-600 text-white disabled:opacity-50"
                >
                  {loading ? "Submitting..." : "Submit Lead"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderStep(step, form, update, updateNested, errors) {
  switch (step) {
    case 0:
      return (
        <div className="space-y-4">
          <Input label="Project Title" value={form.title} onChange={(e) => update("title", e.target.value)} />
          <Error text={errors.title} />

          <div className="grid md:grid-cols-2 gap-4">
            <Select label="Sector" options={SECTORS} value={form.sector} onChange={(e) => update("sector", e.target.value)} />
            <Select
              label="Value Chain Position"
              options={VALUE_CHAIN_POSITIONS}
              value={form.valueChainPosition}
              onChange={(e) => update("valueChainPosition", e.target.value)}
            />
          </div>
          <Error text={errors.sector || errors.valueChainPosition} />

          <div className="grid md:grid-cols-2 gap-4">
            <Select label="Project Stage" options={STAGES} value={form.stage} onChange={(e) => update("stage", e.target.value)} />
            <Input label="Country" value={form.country} onChange={(e) => update("country", e.target.value)} />
          </div>

          <Textarea
            label="Opportunity Summary"
            value={form.summary}
            onChange={(e) => update("summary", e.target.value)}
            placeholder="Describe the opportunity, pilot, or project in a few sentences."
          />
        </div>
      );

    case 1:
      return (
        <div className="space-y-4">
          <Input
            label="Organization Name"
            value={form.organizationName}
            onChange={(e) => update("organizationName", e.target.value)}
          />
          <Error text={errors.organizationName} />

          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Website" value={form.website} onChange={(e) => update("website", e.target.value)} />
            <Select label="User Type" options={USER_TYPES} value={form.userType} onChange={(e) => update("userType", e.target.value)} />
          </div>

          <h3 className="font-semibold pt-4">Primary Contact</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Input
              label="Full Name"
              value={form.contactInfo.name}
              onChange={(e) => updateNested("contactInfo", "name", e.target.value)}
            />
            <Input
              label="Email"
              value={form.contactInfo.email}
              onChange={(e) => updateNested("contactInfo", "email", e.target.value)}
            />
            <Input
              label="Phone"
              value={form.contactInfo.phone}
              onChange={(e) => updateNested("contactInfo", "phone", e.target.value)}
            />
          </div>
          <Error text={errors.contactName || errors.contactEmail} />
        </div>
      );

    case 2:
      return (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Project Value (USD)" value={form.projectValue} onChange={(e) => update("projectValue", e.target.value)} />
            <Input
              label="Funding Required (USD)"
              value={form.fundingRequired}
              onChange={(e) => update("fundingRequired", e.target.value)}
            />
          </div>

          <Select
            label="Funding Source"
            options={FUNDING_SOURCES}
            value={form.fundingSource}
            onChange={(e) => update("fundingSource", e.target.value)}
          />

          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Annual CO₂ Reduction"
              value={form.co2Reduction}
              onChange={(e) => update("co2Reduction", e.target.value)}
            />
            <Input
              label="Jobs Created"
              value={form.jobsCreated}
              onChange={(e) => update("jobsCreated", e.target.value)}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Input
              type="date"
              label="Target Start Date"
              value={form.targetStartDate}
              onChange={(e) => update("targetStartDate", e.target.value)}
            />
            <Input
              type="date"
              label="Commercialization Date"
              value={form.commercializationDate}
              onChange={(e) => update("commercializationDate", e.target.value)}
            />
          </div>

          <Textarea
            label="Additional Comments"
            value={form.comments}
            onChange={(e) => update("comments", e.target.value)}
          />
        </div>
      );

    case 3:
      return (
        <div className="space-y-6">
          <div className="rounded-2xl border-2 border-dashed border-gray-300 p-8 text-center bg-gray-50">
            <input
              id="document-upload"
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                update("documents", files);
              }}
            />

            <label
              htmlFor="document-upload"
              className="cursor-pointer inline-flex items-center px-5 py-3 bg-green-600 text-white rounded-xl"
            >
              Upload Supporting Documents
            </label>

            <p className="text-sm text-gray-600 mt-3">
              Attach pitch decks, financial models, technical specifications,
              certifications, feasibility studies, or images.
            </p>
          </div>

          {form.documents.length > 0 && (
            <div className="rounded-2xl border bg-white p-5">
              <h3 className="font-semibold mb-4">
                Attached Documents ({form.documents.length})
              </h3>
              <div className="space-y-2">
                {form.documents.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between border rounded-xl px-4 py-3"
                  >
                    <div>
                      <div className="font-medium text-sm">{file.name}</div>
                      <div className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        update(
                          "documents",
                          form.documents.filter((_, i) => i !== index)
                        )
                      }
                      className="text-red-600 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );

    case 4:
      return <Review form={form} />;

    default:
      return null;
  }
}

function Review({ form }) {
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border bg-green-50 p-5">
        <h3 className="text-lg font-semibold text-green-800">Review Your Lead</h3>
        <p className="text-sm text-green-700 mt-1">
          Confirm all details before submitting.
        </p>
      </div>

      <ReviewSection
        title="Basic Information"
        items={[
          ["Title", form.title],
          ["Sector", form.sector],
          ["Value Chain Position", form.valueChainPosition],
          ["Stage", form.stage],
          ["Country", form.country],
        ]}
      />

      <ReviewSection
        title="Organization"
        items={[
          ["Organization", form.organizationName],
          ["Website", form.website || "—"],
          ["User Type", form.userType || "—"],
          ["Contact", form.contactInfo.name],
          ["Email", form.contactInfo.email],
        ]}
      />

      <ReviewSection
        title="Documents"
        items={[
          [
            "Attached Files",
            form.documents.length
              ? form.documents.map((f) => f.name).join(", ")
              : "No documents attached",
          ],
        ]}
      />
    </div>
  );
}

function ReviewSection({ title, items }) {
  return (
    <div className="rounded-2xl border bg-white p-5">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="space-y-3">
        {items.map(([label, value]) => (
          <div key={label} className="grid md:grid-cols-3 gap-2 border-b pb-2 last:border-b-0">
            <div className="text-sm font-medium text-gray-600">{label}</div>
            <div className="md:col-span-2 text-sm">{value || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input {...props} className="w-full rounded-xl border px-3 py-2" />
    </div>
  );
}

function Textarea({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <textarea {...props} rows={4} className="w-full rounded-xl border px-3 py-2" />
    </div>
  );
}

function Select({ label, options, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <select {...props} className="w-full rounded-xl border px-3 py-2">
        <option value="">Select {label}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function TagInput({ label, values, onChange }) {
  const [input, setInput] = useState("");

  const addTag = () => {
    const tag = input.trim();
    if (!tag || values.includes(tag)) return;
    onChange([...values, tag]);
    setInput("");
  };

  const removeTag = (tag) => {
    onChange(values.filter((v) => v !== tag));
  };

  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <div className="flex gap-2 mb-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag();
            }
          }}
          className="flex-1 rounded-xl border px-3 py-2"
        />
        <button type="button" onClick={addTag} className="px-4 py-2 bg-green-600 text-white rounded-xl">
          Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {values.map((tag) => (
          <span
            key={tag}
            onClick={() => removeTag(tag)}
            className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm cursor-pointer"
          >
            {tag} ×
          </span>
        ))}
      </div>
    </div>
  );
}

function RangeInput({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <input type="range" className="w-full" {...props} />
    </div>
  );
}

function Error({ text }) {
  if (!text) return null;
  return <p className="text-sm text-red-600 mt-1">{text}</p>;
}
